import { inject } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { isLeft } from "fp-ts/lib/Either";
import { ShareIOSIcon } from "../../../core/components/icons/ShareIOSIcon.component";
import { getCoastLinks, getShowZypeSlugs } from "../lib";
import { COAST_SHOW_TYPE, getCoastShowType } from "../lib/coastContent";
import { CoastShowAudioBoxPlayerLinkLoader } from "../page-blocks/content/coast-show-audiobox/CoastShowAudioBoxPlayerLink.component";
import { CoastShowPayloadResolver } from "../page-blocks/content/CoastContent.types";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { CoastShowShareButtons } from "./CoastShowShareButtons.component";
import type { Store } from "@inferno/renderer-shared-core";
import { reverseRoute } from "@inferno/renderer-shared-core";
import { overflowUnset } from "@inferno/renderer-shared-core";
import { ClickSection } from "@inferno/renderer-shared-core";

interface CoastShowItemDetailsButtonProps {
  content: Webapi.PublishRecordFragment;
  store?: Store;
}
export const CoastShowItemDetailsButton = inject("store")(({ content, store }: CoastShowItemDetailsButtonProps) => {
  const resolvedPayload = CoastShowPayloadResolver.decode(content.payload);
  const [open, setOpen] = useState(false);

  if (isLeft(resolvedPayload) || !store) {
    return null;
  }

  const handleClick = useCallback(() => {
    setOpen(true);
    overflowUnset();
  }, [setOpen]);

  const handleDismiss = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const { summary, fields } = resolvedPayload.right;
  const showType = useMemo(() => getCoastShowType(content), [content]);
  const { site, request } = store;
  const url = site.getShareUrl(request);
  const title = summary?.title || "";
  const hostname = fields.host_name?.value;
  const host = useMemo(() => getCoastLinks(content, "hosts")?.[0], [content]);
  const hostUrl = reverseRoute(store.site, "hosts_detail", { slug: host?.slug }) || "#";

  return (
    <div className="coast-showitem-details">
      {(showType === COAST_SHOW_TYPE.REGULAR || showType === COAST_SHOW_TYPE.CLASSIC) && fields.show_date?.value ? (
        <CoastShowAudioBoxPlayerLinkLoader
          showSlugs={getShowZypeSlugs(fields)}
          showDate={fields.show_date?.value}
          showVolumeIcon={false}
          frontMatter={true}
        />
      ) : null}
      <ClickSection.Provider value="header">
        <button className="shareIOS-icon" onClick={handleClick}>
          <ShareIOSIcon />
        </button>
      </ClickSection.Provider>
      {open ? (
        <CoastShowShareButtons
          url={url}
          title={title}
          desc={summary?.description || ""}
          thumb={content.summary.image || ""}
          hostName={hostname || ""}
          hostUrl={hostUrl || ""}
          callback={handleDismiss}
        />
      ) : null}
    </div>
  );
});
